/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {ChangeDetectionStrategy, Component, DestroyRef, Inject, Input, OnInit, inject, signal} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import * as _ from 'lodash';

import template from './retail-product-list-page.component.html';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';
import {SignalAdminListColumn, SignalAdminResourceListStateFactory} from '../common/controller/signal-admin-resource-list-state';
import {SignalAdminResourceApi, SignalAdminResourceModel} from '../common/controller/signal-admin-resource-api';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {
  RetailProductCreativesModalComponent,
  RetailProductCreativesModalResult
} from './retail-product-creatives-modal.component';
import {RetailProductConversionTrackingModalComponent} from './retail-product-conversion-tracking-modal.component';
import {LayoutApiService} from '../admin/layout/layout-api.service';
import {ProductFeedApiService} from '../common/controller/product-feed-api.service';
import {StateService} from '@uirouter/core';
import {RetailProductListFiltersComponent} from './retail-product-list-filters.component';

interface RetailProductModel extends SignalAdminResourceModel {
  [key: string]: any;
}

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED, RetailProductListFiltersComponent],
  selector: 'adn-retail-product-list-page',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RetailProductListPageComponent implements OnInit {
  @Input() selectEnabled = false;
  @Input() createHook: (item: any, selection?: Record<string, RetailProductModel>) => void | Promise<any>;
  @Input() showConversionTracking = false;
  @Input()
  set readOnly(value: boolean) {
    this.list.readOnly.set(value);
  }

  readonly itemStateName = 'app.adv.retail-products.retail-product';
  readonly creatingCreatives = signal(false);
  private readonly filterModelState = signal<any>({});
  private readonly refLayoutState = signal<any>(null);

  get filterModel(): any {
    return this.filterModelState();
  }

  get refLayout(): any {
    return this.refLayoutState();
  }

  set refLayout(value: any) {
    this.refLayoutState.set(value);
  }
  readonly merchantGuide = this.localNetworkInfo.getMerchantGuide();
  readonly showMerchantColumn: boolean;

  private readonly productFeedApi = inject(ProductFeedApiService);
  readonly list = inject(SignalAdminResourceListStateFactory).create<RetailProductModel>(
    this.productFeedApi as unknown as SignalAdminResourceApi<RetailProductModel>,
    {
      listId: 'fullRetailProductList',
      defaultOrderBy: 'itemTitle',
      transformResults: (items) => items.map((item) => ({
        ...item,
        id: item.id || `${item.merchantId}:${item.itemId}`
      }))
    },
    inject(DestroyRef)
  );

  constructor(
    @Inject(LayoutApiService) private readonly Layout: LayoutApiService,
    private readonly $state: StateService,
    private readonly toastr: ToastrService,
    private readonly angularModalService: AngularModalService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    const networkInfo = this.localNetworkInfo;
    const activeFeedCount = _.filter(networkInfo.getInfo('productFeedProductCounts') || [], (feed: any) => {
      return feed.productCount > 0;
    }).length;
    this.showMerchantColumn = _.size(networkInfo.getInfo('merchants')) > 1 || activeFeedCount > 1;
    this.filterModel.merchantGuide = this.merchantGuide;
    this.filterModel.makeRetailProductQuery = (type: string, clear = false) => {
      const working = 'working' + _.upperFirst(type);
      this.filterModel[type] = clear ? null : this.filterModel[working];
      if (clear) {
        this.filterModel[working] = null;
      }
      this.list.setFilterParams('retailProduct', {
        ...(this.filterModel.merchant ? {merchant: this.filterModel.merchant} : {}),
        ...(this.filterModel.brand ? {brand: this.filterModel.brand} : {}),
        ...(this.filterModel.colour ? {colour: this.filterModel.colour} : {})
      });
    };
  }

  get columns(): SignalAdminListColumn[] {
    return [
      {param: 'itemTitle', label: 'name'},
      ...(this.showMerchantColumn ? [{param: '', label: 'merchant', sortable: false}] : []),
      {param: 'brand'},
      {param: '', label: 'image', sortable: false},
      {param: 'price'},
      ...(this.showConversionTracking ? [{param: '', label: 'conversionPixel', sortable: false}] : [])
    ];
  }

  ngOnInit(): void {
    this.loadFacets();
    this.loadLayout();
    this.list.load();
  }

  merchantLink(link: string): string {
    const value = (link || '').trim();
    if (/^https?:\/\//i.test(value)) {
      return value;
    }
    if (value.startsWith('//')) {
      return 'https:' + value;
    }
    return value ? 'https://' + value.replace(/^\/+/, '') : '';
  }

  selected(item: RetailProductModel): void {
    if (_.isFunction(this.createHook)) {
      this.createHook(item);
    }
  }

  openConversionTracking(product: RetailProductModel): void {
    this.angularModalService.show(RetailProductConversionTrackingModalComponent, {
      initialState: {product},
      class: 'modal-md'
    });
  }

  createCreatives(): void {
    if (this.creatingCreatives()) {
      return;
    }
    if (_.isFunction(this.createHook)) {
      this.creatingCreatives.set(true);
      Promise.resolve(this.createHook(this.refLayout, this.list.selected())).catch(() => undefined).finally(() => {
        this.creatingCreatives.set(false);
      });
      return;
    }
    const modalRef = this.angularModalService.show(RetailProductCreativesModalComponent, {
      initialState: {bulkSelection: _.cloneDeep(this.list.selected())},
      class: 'modal-md'
    });
    (modalRef.content as unknown as RetailProductCreativesModalComponent).onCreated.subscribe(
      (result: RetailProductCreativesModalResult) => {
        if (result.destination && result.model?.id) {
          this.$state.go('app.adv.line-items.line-item', {id: result.model.id}, {inherit: false});
        } else {
          this.toastr.success('Creatives were successfully created.', 'Creatives Created');
        }
      }
    );
  }

  private loadFacets(): void {
    const applyFacets = () => {
      const facets = this.localNetworkInfo.getInfo('retailProductFacets');
      this.filterModelState.set({
        ...this.filterModel,
        merchants: _.filter(facets.merchants, (facet: any) => facet && facet.id),
        colours: _.filter(facets.colours, (facet: any) => facet.facet),
        brands: _.filter(facets.brands, (facet: any) => facet.facet)
      });
    };
    const facets = this.localNetworkInfo.getInfo('retailProductFacets');
    if (!facets || facets.merchants.length < 1) {
      this.productFeedApi.getFacets().subscribe((loaded) => {
        this.localNetworkInfo.setRetailProductFacets(loaded);
        applyFacets();
      });
    } else {
      applyFacets();
    }
  }

  private loadLayout(): void {
    if (!_.isFunction(this.createHook)) {
      return;
    }
    firstValueFrom(this.Layout.listOptions()).then((page: any) => {
      this.refLayout = _.find(page.results, {detectedCategory: 'RETAIL'});
    });
  }
}
